<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IconAdd, IconClose, IconCopy } from '@/components/design/icons';
import type {
  NonPlatformUserInDb,
  ReviewProjectInDb,
  ReviewProjectNonPlatformUserLinkInDb,
} from '@/api';
import UserInitialsAvatar from '@/components/UserInitialsAvatar.vue';
import { ReviewProjectService } from '@/services/reviewProjectService';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaInputGroup from '@/components/design/base/inputs/GaiaInputGroup.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  project: ReviewProjectInDb;
}>();

const emit = defineEmits<{
  (e: 'updated', project: ReviewProjectInDb): void;
}>();

const message = useGaiaToast();
const router = useRouter();

// ==================== Project-level public switches ====================
const publicQaEnabled = ref<boolean>(props.project.public_qa_enabled);
const publicDataRoomUploadEnabled = ref<boolean>(props.project.public_data_room_upload_enabled);
const savingPublicFlags = ref<boolean>(false);

watch(
  () => props.project.public_qa_enabled,
  value => {
    publicQaEnabled.value = value;
  }
);
watch(
  () => props.project.public_data_room_upload_enabled,
  value => {
    publicDataRoomUploadEnabled.value = value;
  }
);

async function handleTogglePublicQa(value: boolean): Promise<void> {
  publicQaEnabled.value = value;
  savingPublicFlags.value = true;
  try {
    const updated = await ReviewProjectService.update(props.project.uid, {
      public_qa_enabled: value,
    });
    emit('updated', updated);
  } catch (error) {
    publicQaEnabled.value = props.project.public_qa_enabled;
    message.error(error instanceof Error ? error.message : 'Failed to update project.');
  } finally {
    savingPublicFlags.value = false;
  }
}

async function handleTogglePublicDataRoomUpload(value: boolean): Promise<void> {
  publicDataRoomUploadEnabled.value = value;
  savingPublicFlags.value = true;
  try {
    const updated = await ReviewProjectService.update(props.project.uid, {
      public_data_room_upload_enabled: value,
    });
    emit('updated', updated);
  } catch (error) {
    publicDataRoomUploadEnabled.value = props.project.public_data_room_upload_enabled;
    message.error(error instanceof Error ? error.message : 'Failed to update project.');
  } finally {
    savingPublicFlags.value = false;
  }
}

// ==================== Public page link ====================
// The public page addresses the project by its external access uid; the
// internal uid is never part of the external surface.
const publicUrl =
  window.location.origin +
  router.resolve({
    name: 'external-review-project',
    params: { externalUid: props.project.external_access_uid },
  }).href;

async function handleCopyPublicUrl(): Promise<void> {
  // lib.dom types navigator.clipboard as always present, but it is missing in
  // insecure contexts; the `in` check keeps the runtime guard without lying to TS.
  if ('clipboard' in navigator) {
    await navigator.clipboard.writeText(publicUrl);
    message.success('Copied to clipboard');
  } else {
    message.error('Clipboard is not available in this browser');
  }
}

// ==================== External users ====================
const externalUsers = ref<ReviewProjectNonPlatformUserLinkInDb[]>([]);
const externalUsersLoading = ref<boolean>(false);

function avatarUser(user: NonPlatformUserInDb): { full_name: string; email: string; uid: string } {
  return { full_name: user.full_name ?? '', email: user.email, uid: user.uid };
}

async function fetchExternalUsers(): Promise<void> {
  externalUsersLoading.value = true;
  try {
    externalUsers.value = await ReviewProjectService.listExternalUsers(props.project.uid);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load external users.');
  } finally {
    externalUsersLoading.value = false;
  }
}

const newEmail = ref<string>('');
const newName = ref<string>('');
const addingUser = ref<boolean>(false);

async function handleAddExternalUser(): Promise<void> {
  const email = newEmail.value.trim();
  if (email.length === 0) return;
  addingUser.value = true;
  try {
    await ReviewProjectService.upsertExternalUser(props.project.uid, {
      email,
      full_name: newName.value.trim() || null,
      enable_qa_answering: true,
      enable_data_room_upload: true,
    });
    newEmail.value = '';
    newName.value = '';
    await fetchExternalUsers();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add external user.');
  } finally {
    addingUser.value = false;
  }
}

async function handleToggleCapability(
  link: ReviewProjectNonPlatformUserLinkInDb,
  capability: 'enable_qa_answering' | 'enable_data_room_upload',
  value: boolean
): Promise<void> {
  const previous = link[capability];
  link[capability] = value;
  try {
    // The upsert's flags default to false on the backend, so always send the full state.
    const updated = await ReviewProjectService.upsertExternalUser(props.project.uid, {
      email: link.non_platform_user.email,
      full_name: link.non_platform_user.full_name,
      enable_qa_answering: link.enable_qa_answering,
      enable_data_room_upload: link.enable_data_room_upload,
    });
    link.enable_qa_answering = updated.enable_qa_answering;
    link.enable_data_room_upload = updated.enable_data_room_upload;
  } catch (error) {
    link[capability] = previous;
    message.error(error instanceof Error ? error.message : 'Failed to update external user.');
  }
}

async function handleRemoveExternalUser(link: ReviewProjectNonPlatformUserLinkInDb): Promise<void> {
  try {
    await ReviewProjectService.removeExternalUser(props.project.uid, link.non_platform_user.uid);
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    message.success(`Removed ${link.non_platform_user.full_name || link.non_platform_user.email}`);
    await fetchExternalUsers();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to remove external user.');
  }
}

onMounted(() => {
  void fetchExternalUsers();
});
</script>

<template>
  <n-space vertical :size="12">
    <gaia-checkbox
      :checked="publicQaEnabled"
      :disabled="savingPublicFlags"
      @update:checked="handleTogglePublicQa"
    >
      Public Q&amp;A enabled
    </gaia-checkbox>
    <gaia-checkbox
      :checked="publicDataRoomUploadEnabled"
      :disabled="savingPublicFlags"
      @update:checked="handleTogglePublicDataRoomUpload"
    >
      Public data room upload enabled
    </gaia-checkbox>

    <gaia-input-group>
      <gaia-input :value="publicUrl" readonly size="small" />
      <n-button size="small" @click="handleCopyPublicUrl">
        <template #icon>
          <n-icon><IconCopy /></n-icon>
        </template>
        Copy
      </n-button>
    </gaia-input-group>

    <n-flex :size="8" :wrap="false" align="center">
      <gaia-input
        v-model:value="newEmail"
        size="small"
        placeholder="Email"
        style="flex: 1; min-width: 150px"
        @keydown.enter="handleAddExternalUser"
      />
      <gaia-input
        v-model:value="newName"
        size="small"
        placeholder="Name (optional)"
        style="flex: 1; min-width: 120px"
        @keydown.enter="handleAddExternalUser"
      />
      <n-button
        size="small"
        :disabled="newEmail.trim().length === 0"
        :loading="addingUser"
        @click="handleAddExternalUser"
      >
        <template #icon>
          <n-icon><IconAdd /></n-icon>
        </template>
        Add
      </n-button>
    </n-flex>

    <n-spin :show="externalUsersLoading">
      <n-list v-if="externalUsers.length > 0" bordered>
        <n-list-item v-for="link in externalUsers" :key="link.non_platform_user.uid">
          <n-flex align="center" :size="12" :wrap="false">
            <UserInitialsAvatar :user="avatarUser(link.non_platform_user)" :show-tooltip="false" />
            <n-flex vertical :size="0">
              <n-text>{{
                link.non_platform_user.full_name || link.non_platform_user.email
              }}</n-text>
              <n-text v-if="link.non_platform_user.full_name" :depth="3" style="font-size: 12px">
                {{ link.non_platform_user.email }}
              </n-text>
            </n-flex>
          </n-flex>
          <template #suffix>
            <n-flex align="center" :size="16" :wrap="false">
              <n-flex align="center" :size="6" :wrap="false">
                <!-- nowrap: n-list-item__suffix has flex-basis 0, letters wrap vertically otherwise -->
                <n-text :depth="3" style="font-size: 12px; white-space: nowrap">Q&amp;A</n-text>
                <gaia-switch
                  size="small"
                  :value="link.enable_qa_answering"
                  @update:value="
                    (value: boolean) => handleToggleCapability(link, 'enable_qa_answering', value)
                  "
                />
              </n-flex>
              <n-flex align="center" :size="6" :wrap="false">
                <n-text :depth="3" style="font-size: 12px; white-space: nowrap">Upload</n-text>
                <gaia-switch
                  size="small"
                  :value="link.enable_data_room_upload"
                  @update:value="
                    (value: boolean) =>
                      handleToggleCapability(link, 'enable_data_room_upload', value)
                  "
                />
              </n-flex>
              <n-button quaternary circle size="small" @click="handleRemoveExternalUser(link)">
                <template #icon>
                  <n-icon><IconClose /></n-icon>
                </template>
              </n-button>
            </n-flex>
          </template>
        </n-list-item>
      </n-list>
      <n-empty v-else-if="!externalUsersLoading" description="No external users yet" />
    </n-spin>
  </n-space>
</template>
